import * as React from "react";
import Select, {
  components,
  type GroupBase,
  type FormatOptionLabelMeta,
  type OptionProps,
  type SingleValue,
} from "react-select";
import { Check, Plus } from "lucide-react";
import { cn } from "../../lib/utils";

export interface SelectSearchOption {
  value: string;
  label: string;
  description?: string;
  keywords?: string[];
  disabled?: boolean;
  badge?: string;
}

export type SearchableSelectOption = SelectSearchOption;

interface SelectSearchProps<
  TOption extends SelectSearchOption = SelectSearchOption,
> {
  value: string;
  options: TOption[];
  placeholder?: string;
  searchHint?: string;
  emptyMessage?: string;
  createLabel?: (value: string) => string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  noCreateWhenExactMatch?: boolean;
  onValueChange: (value: string) => void;
  onSearchChange?: (value: string) => void;
  onSelect?: (option: TOption) => void;
  onCreate?: (value: string) => void;
  renderLabel?: (option: TOption) => React.ReactNode;
}

type SelectOption<TOption extends SelectSearchOption> = TOption & {
  __isCreateOption?: boolean;
};

const formatDefaultLabel = (option: SelectSearchOption) => (
  <div className="min-w-0 flex-1">
    <div className="truncate font-medium text-[#0F172A]">{option.label}</div>
    {option.description ? (
      <div className="truncate text-xs text-[#6A7282]">
        {option.description}
      </div>
    ) : null}
  </div>
);

const CreateOptionContent = ({ label }: { label: string }) => (
  <div className="flex items-center gap-2 text-[#004232]">
    <Plus className="h-4 w-4" />
    <span>{label}</span>
  </div>
);

export function SelectSearch<
  TOption extends SelectSearchOption = SelectSearchOption,
>({
  value,
  options,
  placeholder = "Search or select...",
  searchHint = "Search existing items or create a new one",
  emptyMessage = "No results found.",
  createLabel = (nextValue) => `Create "${nextValue}"`,
  disabled = false,
  className,
  inputClassName,
  dropdownClassName,
  noCreateWhenExactMatch = true,
  onValueChange,
  onSearchChange,
  onSelect,
  onCreate,
  renderLabel,
}: SelectSearchProps<TOption>) {
  const [inputValue, setInputValue] = React.useState("");

  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  const normalizedInput = inputValue?.trim().toLowerCase();

  const filteredOptions = React.useMemo(() => {
    if (!normalizedInput) {
      return options;
    }

    return options.filter((option) => {
      const haystacks = [
        option.label,
        option.description || "",
        ...(option.keywords || []),
      ];

      return haystacks.some((item) =>
        item.toLowerCase().includes(normalizedInput),
      );
    });
  }, [normalizedInput, options]);

  const hasExactMatch = React.useMemo(
    () =>
      options.some(
        (option) => option.label.trim().toLowerCase() === normalizedInput,
      ),
    [normalizedInput, options],
  );

  const canCreate =
    Boolean(onCreate) &&
    Boolean(normalizedInput) &&
    (!noCreateWhenExactMatch || !hasExactMatch);

  const selectOptions = React.useMemo(() => {
    const baseOptions = filteredOptions as SelectOption<TOption>[];

    if (!canCreate) {
      return baseOptions;
    }

    return [
      ...baseOptions,
      {
        value: `__create__:${inputValue.trim()}`,
        label: createLabel(inputValue.trim()),
        description: searchHint,
        __isCreateOption: true,
      } as SelectOption<TOption>,
    ];
  }, [canCreate, createLabel, filteredOptions, inputValue, searchHint]);

  const selectedValue = selectedOption
    ? (selectedOption as SelectOption<TOption>)
    : null;

  const handleChange = (nextValue: SingleValue<SelectOption<TOption>>) => {
    if (!nextValue) {
      setInputValue("");
      onValueChange("");
      return;
    }

    if (nextValue.__isCreateOption) {
      const nextLabel = inputValue.trim();
      if (nextLabel && onCreate) {
        onCreate(nextLabel);
      }
      return;
    }

    setInputValue("");
    onValueChange(nextValue.value);
    onSelect?.(nextValue as TOption);
  };

  const formatOptionLabel = (
    option: SelectOption<TOption>,
    meta: FormatOptionLabelMeta<SelectOption<TOption>>,
  ) => {
    const labelContent = renderLabel
      ? renderLabel(option as TOption)
      : formatDefaultLabel(option);

    if (meta.context === "value") {
      return labelContent;
    }

    return (
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="min-w-0 flex-1">{labelContent}</div>
        {option.badge ? (
          <span className="rounded-full bg-[#F3FBF6] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-[#00A86B]">
            {option.badge}
          </span>
        ) : null}
      </div>
    );
  };

  const renderOptionBody = (option: SelectOption<TOption>) => {
    const labelContent = renderLabel
      ? renderLabel(option as TOption)
      : formatDefaultLabel(option);

    return (
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">{labelContent}</div>
        {option.badge ? (
          <span className="rounded-full bg-[#F3FBF6] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-[#00A86B]">
            {option.badge}
          </span>
        ) : null}
      </div>
    );
  };

  const Option = (
    props: OptionProps<
      SelectOption<TOption>,
      false,
      GroupBase<SelectOption<TOption>>
    >,
  ) => {
    const { data, isSelected } = props;

    if (data.__isCreateOption) {
      return (
        <components.Option {...props}>
          <CreateOptionContent label={data.label} />
        </components.Option>
      );
    }

    return (
      <components.Option {...props}>
        <div className="flex items-start gap-3">
          {renderOptionBody(data)}
          <div className="flex items-center gap-2 pl-2">
            {isSelected ? <Check className="h-4 w-4 text-[#004232]" /> : null}
          </div>
        </div>
      </components.Option>
    );
  };

  return (
    <div className={cn("w-full", className)}>
      <Select<SelectOption<TOption>, false, GroupBase<SelectOption<TOption>>>
        value={selectedValue}
        options={selectOptions}
        isDisabled={disabled}
        inputValue={inputValue}
        onInputChange={(nextValue, meta) => {
          if (meta.action === "input-change") {
            setInputValue(nextValue);
            onSearchChange?.(nextValue);
          }
          if (meta.action === "menu-close") {
            setInputValue("");
          }
          return nextValue;
        }}
        onChange={handleChange}
        placeholder={placeholder}
        isClearable={true}
        isSearchable
        filterOption={null}
        noOptionsMessage={() => emptyMessage}
        formatOptionLabel={formatOptionLabel}
        components={{
          Option,
          IndicatorSeparator: () => null,
        }}
        classNames={{
          control: () =>
            "min-h-11 rounded-lg border border-[#DDEBE3] bg-white text-sm shadow-none hover:border-[#B9D3C6]",
          valueContainer: () => "px-3 py-1.5",
          input: () => cn("text-sm text-[#0F172A]", inputClassName),
          placeholder: () => "text-sm text-[#94A3B8]",
          singleValue: () => "text-sm text-[#0F172A]",
          menu: () =>
            cn(
              "mt-2 overflow-hidden rounded-xl border border-[#DDEBE3] bg-white shadow-[0_24px_80px_rgba(15,23,42,0.12)]",
              dropdownClassName,
            ),
          menuList: () =>
            "max-h-[min(32rem,calc(100vh-14rem))] overflow-y-auto p-1",
          option: ({ isFocused, isSelected, data }) =>
            cn(
              "cursor-pointer rounded-lg px-3 py-2 text-sm",
              isFocused && "bg-[#F3FBF6]",
              isSelected && "bg-[#EAF7F0]",
              (data as SelectOption<TOption>).__isCreateOption &&
                "border-t border-[#E6EFE9] text-[#004232]",
            ),
          noOptionsMessage: () =>
            "px-3 py-6 text-center text-sm text-muted-foreground",
          clearIndicator: () => "hidden",
          dropdownIndicator: () => "text-[#6A7282]",
        }}
        styles={{
          control: (base, state) => ({
            ...base,
            boxShadow: "none",
            minHeight: 44,
            borderColor: state.isFocused ? "#B9D3C6" : "#DDEBE3",
            backgroundColor: "#fff",
            cursor: state.isDisabled ? "not-allowed" : "default",
          }),
          menuPortal: (base) => ({ ...base, zIndex: 50 }),
          dropdownIndicator: (base) => ({
            ...base,
            paddingLeft: 0,
            paddingRight: 12,
          }),
          indicatorSeparator: (base) => ({ ...base, display: "none" }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? "#EAF7F0"
              : state.isFocused
                ? "#F3FBF6"
                : "transparent",
            color: "#0F172A",
          }),
          menu: (base) => ({
            ...base,
            overflow: "hidden",
            borderRadius: 14,
          }),
          noOptionsMessage: (base) => ({
            ...base,
            paddingTop: 24,
            paddingBottom: 24,
          }),
        }}
      />
    </div>
  );
}

export function SearchableSelect<
  TOption extends SelectSearchOption = SelectSearchOption,
>(props: SelectSearchProps<TOption>) {
  return <SelectSearch {...props} />;
}
